import React, { useState } from 'react'
import Button from 'components/button'
import Select from 'components/select'
import IntegrationCard from 'views/integrations/components/IntegrationCard'
import { validateSourceUrl } from 'helpers/utils'
import { rememberReviztoRegion } from 'helpers/integration'
import { APP_URL, IntegrationProviders, INTEGRATION_REGION_PLACEHOLDER, REVIZTO_REGIONS } from 'app-constants'

interface ReviztoConnectionProps {
  connected: boolean
  connectingAccount: boolean
  disconnectAccount: (provider: string) => void
  // The OAuth authorize URL template from /integrations/redirects; its host
  // contains INTEGRATION_REGION_PLACEHOLDER.
  redirectUrl: string
  // Browser navigation to Revizto's sign-in; injectable because jsdom can't stub window.location.
  redirect?: (url: string) => void
}

// Revizto connects through the standard OAuth sign-in, but it is deployed per
// region and tokens only work on the host that issued them, so the user picks
// their account's region first.
const ReviztoConnection: React.FC<ReviztoConnectionProps> = ({
  connected,
  connectingAccount,
  disconnectAccount,
  redirectUrl,
  redirect = (url) => window.location.assign(url)
}) => {
  const [region, setRegion] = useState<string>('')

  const connect = () => {
    // The callback returns to the Integrations page, which hands the region to
    // the token exchange (it must hit the same regional host).
    rememberReviztoRegion(region)
    redirect(redirectUrl.replace(INTEGRATION_REGION_PLACEHOLDER, region))
  }

  return (
    <IntegrationCard
      provider={IntegrationProviders.REVIZTO}
      logo={validateSourceUrl(`${APP_URL}/assets/images/revizto.webp`)}
      connected={connected}
      connectingAccount={connectingAccount}
      disconnectAccount={disconnectAccount}
    >
      {!connected && (
        // One row, so the tile keeps the same height as the others.
        <div className="flex w-full min-w-0 items-center gap-1.5">
          <div className="min-w-0 flex-1">
            <Select
              options={Object.keys(REVIZTO_REGIONS).map((regionKey) => ({
                value: regionKey,
                label: REVIZTO_REGIONS[regionKey]
              }))}
              optionsSelected={region ? [region] : []}
              onOptionClick={(option) => setRegion(option)}
              placeholder="Select Region"
              menuWidth={280}
              className="py-[7px] px-2.5"
            />
          </div>
          {region !== '' && (
            <Button
              className="shrink-0"
              data-tour="integration-connect-account"
              disabled={connectingAccount}
              onClick={connect}
            >
              Connect
            </Button>
          )}
        </div>
      )}
    </IntegrationCard>
  )
}

export default ReviztoConnection
